// External Imports
import { useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import { MdAdd, MdRefresh } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import LoadingCard from "../UI/LoadingCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import { UtilityContext } from "../../context/util-context";
import { useLocation } from "react-router-dom";
import Radio from "../UI/Radio";
import { isoDateToString } from "../../util/format-date";
import PaginationMenu from "../UI/PaginationMenu";
import SearchBar from "../UI/SearchBar";
import DateFilter from "../UI/DateFilter";
import FilterMenu from "../UI/FilterMenu";
import ListLabels from "../UI/ListLabels";
import ExportCsvButton from "../UI/ExportCsvButton";

// Internal Imports

/** ========================================================================
 * * Repossessions Component
 * This component renders a list of all claims.
 * ========================================================================== */

export default function Repossessions() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    status: "All",
    sort: "dateCreated",
    date: "dateCreated",
    query: "",
    sd: "",
    ed: "",
    limit: 50,
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key])
  );

  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [filter, setFilter] = useState(paramFilters);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);
      // Send Request
      const data = await requestAdminAPI("search", {
        table: "repossession",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/repossessions?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setRecords(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== FILTER OPTIONS ===================
  const filterOptions = [
    {
      label: "Status",
      options: ["All", "Initiated", "Posted", "Approved", "Recovered", "Cancelled"],
      selected: filter.status,
    },
    {
      label: "Date",
      options: [
        { label: "Date Initiated", value: "dateCreated" },
        { label: "Date Posted", value: "datePosted" },
        { label: "Date Approved", value: "dateApproved" },
        { label: "Date Recovered", value: "dateRecovered" },
        { label: "Date Cancelled", value: "dateCancelled" },
      ],
      selected: filter.date,
    },
  ];

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading
        title="Repossessions"
        text="To initiate a new vehicle repossession, go to the booking information page and click initiate repossession."
      >
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdRefresh className="icon" onClick={fetchRecords} />
        <ExportCsvButton array={records} filename="repossessions" />
      </Heading>
      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />
      {loading ? (
        <LoadingCard columns={5} max={4} />
      ) : records.length > 0 ? (
        <div className="list">
          <div className="labels lg:grid-cols-9">
            <ListLabels
              labels={[
                { classes: "w-24", name: " " },
                { name: "Initiated", key: "dateCreated" },
                { classes: "lg:col-span-2", name: "Vehicle" },
                { classes: "lg:col-span-2", name: "Customer" },
                { name: "Posted", key: "datePosted" },
                { name: "Approved", key: "dateApproved" },
                { name: "Recovered", key: "dateRecovered" },
              ]}
              selected="Date Created"
              setSelected={(s) => setFilter({ ...filter, sort: s })}
            />
          </div>
          {records.map((repo, idx) => (
            <RepossessionCard key={idx} repo={repo} navigate={navigate} />
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        count={records.length}
        limit={filter.limit}
        setLimit={(l) => setFilter({ ...filter, limit: l })}
        loading={loading}
      ></PaginationMenu>
    </div>
  );
}

const RepossessionCard = ({ repo, navigate }) => {
  return (
    <div
      onClick={(e) => navigate(`/repossessions/info?id=${repo.id}`, e)}
      className={`${
        repo?.status === "initiated"
          ? "border-yellow-600"
          : repo.status === "approved"
          ? "border-red-600"
          : repo.status === "posted"
          ? "border-blue-500"
          : repo.status === "cancelled"
          ? "border-stone-400"
          : "border-green-600"
      } grid lg:grid-cols-9 pill`}
    >
      <img src={repo.vehicle.cornerImage} className="object-contain w-24" />
      <p className="text-stone-400">{isoDateToString(repo.dateCreated)}</p>
      <p className="font-semibold lg:col-span-2">
        {repo.vehicle.year} {repo.vehicle.make.name} {repo.vehicle.model}{" "}
        {repo.vehicle.vin.substring(13, 17)}
      </p>
      <p className="font-semibold lg:col-span-2">
        {repo.customer.firstName} {repo.customer.lastName}
      </p>
      <p className="text-stone-400">{isoDateToString(repo.datePosted)}</p>
      <p className="text-stone-400">{isoDateToString(repo.dateApproved)}</p>
      <p className="text-stone-400">{isoDateToString(repo.dateRecovered)}</p>
    </div>
  );
};
